import { form } from '@angular/forms/signals';
import type { FieldTree } from '@angular/forms/signals';
import type { WritableSignal } from '@angular/core';
import type {
  CreatePersonInput,
  IPerson,
  PersonFormDto,
  UpdatePersonInput,
} from '../../shared';
import { personFormSchema } from '../utils/person-form';

export type PersonFormModel = PersonFormDto & {
  organizationId?: string;
};

export function createPersonFieldTree(
  model: WritableSignal<PersonFormModel>,
): FieldTree<PersonFormModel> {
  return form(model, personFormSchema);
}

function toFormString(value: string | number | null | undefined): string {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value);
}

function toOptionalString(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function toOptionalNumber(value: string | undefined): number | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
}

export class PersonFormFactory {
  static initialModel(
    initial?: Partial<IPerson>,
    organizationId?: string,
  ): PersonFormModel {
    return {
      id: initial?.id,
      title: initial?.title ?? '',
      firstName: initial?.firstName ?? '',
      lastName: initial?.lastName ?? '',
      email: initial?.email ?? '',
      phone: initial?.phone ?? '',
      privateMobile: initial?.privateMobile ?? '',
      street: initial?.street ?? '',
      postalCode: initial?.postalCode ?? '',
      city: initial?.city ?? '',
      country: initial?.country ?? '',
      socialSecurityNumber: initial?.socialSecurityNumber ?? '',
      personnelNumber: initial?.personnelNumber ?? '',
      vacationDays: toFormString(initial?.vacationDays),
      company: initial?.company ?? '',
      carPlate: initial?.carPlate ?? '',
      tags: initial?.tags ?? '',
      additionalEmail: initial?.additionalEmail ?? '',
      userId: initial?.userId ?? undefined,
      type: initial?.type ?? 'employee',
      preferredLanguage: initial?.preferredLanguage ?? undefined,
      organizationId,
    };
  }

  static toCreatePayload(formValue: PersonFormModel): CreatePersonInput {
    return {
      title: toOptionalString(formValue.title),
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phone: toOptionalString(formValue.phone),
      privateMobile: toOptionalString(formValue.privateMobile),
      street: toOptionalString(formValue.street),
      postalCode: toOptionalString(formValue.postalCode),
      city: toOptionalString(formValue.city),
      country: toOptionalString(formValue.country),
      socialSecurityNumber: toOptionalString(formValue.socialSecurityNumber),
      personnelNumber: toOptionalString(formValue.personnelNumber),
      vacationDays: toOptionalNumber(formValue.vacationDays),
      company: toOptionalString(formValue.company),
      carPlate: toOptionalString(formValue.carPlate),
      tags: toOptionalString(formValue.tags),
      additionalEmail: toOptionalString(formValue.additionalEmail),
      userId: formValue.userId || undefined,
      type: formValue.type,
      preferredLanguage: formValue.preferredLanguage || undefined,
      organizationId: formValue.organizationId || undefined,
    };
  }

  static toUpdatePayload(formValue: PersonFormModel): UpdatePersonInput {
    if (!formValue.id) {
      throw new Error('ID is required for update operations');
    }

    return {
      id: formValue.id,
      title: toOptionalString(formValue.title),
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phone: toOptionalString(formValue.phone),
      privateMobile: toOptionalString(formValue.privateMobile),
      street: toOptionalString(formValue.street),
      postalCode: toOptionalString(formValue.postalCode),
      city: toOptionalString(formValue.city),
      country: toOptionalString(formValue.country),
      socialSecurityNumber: toOptionalString(formValue.socialSecurityNumber),
      personnelNumber: toOptionalString(formValue.personnelNumber),
      vacationDays: toOptionalNumber(formValue.vacationDays),
      company: toOptionalString(formValue.company),
      carPlate: toOptionalString(formValue.carPlate),
      tags: toOptionalString(formValue.tags),
      additionalEmail: toOptionalString(formValue.additionalEmail),
      userId: formValue.userId || undefined,
      type: formValue.type,
      preferredLanguage: formValue.preferredLanguage || undefined,
      organizationId: formValue.organizationId || undefined,
    };
  }
}
